import { useContext, useMemo } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { GroupDefinition, TabDefinition, TabsDefinition, TabGroup } from './types';
import Jnx from '../../util/jnx';

export function useTabGrouping({
  tabs,
  groups: propGroups,
  defaultGroup,
}: {
  tabs: TabsDefinition;
  groups?: Record<string, GroupDefinition>;
  defaultGroup?: string;
}): {
  tabGroups: TabGroup[];
  ungroupedTabs: TabDefinition[];
} {
  const auth = useContext(AuthContext);
  const { permissions } = (auth?.user as any) || {};

  return useMemo(() => {
    const groupdefs = propGroups ?? {};
    const tabdefs: TabsDefinition = tabs ?? {};

    let lastGroupOrder = 0;
    const ungroupedTabs: TabDefinition[] = [];
    let groups = Object.entries(tabdefs).reduce(
      (accum, [tag, unnamedDef]) => {
        const def = { ...unnamedDef, slug: tag };
        if (!def.name) {
          def.name = tag;
        }

        if (
          !def.noTab &&
          (def.permission ? permissions && permissions[def.permission] : true) &&
          (def.jnx ? (new Jnx(def.jnx) as any).eval(auth || {}, '') : true)
        ) {
          const groupName = def.group ?? defaultGroup;

          if (groupName === undefined) {
            ungroupedTabs.push(def);
          } else {
            if (!accum.index[groupName]) {
              const groupdef: GroupDefinition = groupdefs[groupName] ?? { name: groupName };
              accum.index[groupName] = {
                name: groupdef.name,
                icon: groupdef.icon,
                order: groupdef.order ?? lastGroupOrder + 1,
                buttons: [],
              };
              lastGroupOrder += 1;
              accum.groups.push(accum.index[groupName]);
            }

            accum.index[groupName].buttons.push({ tag, def });
          }
        }

        return accum;
      },
      { groups: [], index: {} } as { groups: TabGroup[]; index: Record<string, TabGroup> }
    ).groups;

    groups.forEach(group => {
      group.buttons.sort((a, b) => (a.def.order ?? 100) - (b.def.order ?? 100));
    });
    groups.sort((a, b) => (a.order ?? 100) - (b.order ?? 100));

    return { tabGroups: groups, ungroupedTabs };
  }, [permissions, auth, tabs, propGroups]);
}
